import {
  createElement,
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useState,
  ReactNode,
} from 'react';
import { ItemIdentifier } from '@sas/nova-core/services/drive';

/**
 * Array of ItemIdentifiers representing the currently selected content.
 */
export type ContentSelectionContextValue = ItemIdentifier[];

export const ContentSelectionStateContext = createContext<ContentSelectionContextValue | undefined>(
  undefined
);
export const ContentSelectionStateDispatch = createContext<
  Dispatch<SetStateAction<ContentSelectionContextValue>> | undefined
>(undefined);

/**
 * Returns the current selection for the nearest provider, or undefined if not under a ContentSelectionProvider.
 */
export const useContentSelectionState = () => {
  return useContext(ContentSelectionStateContext);
};

/**
 * Dispatch a new selection to all components consuming this provider. Will return undefined if not
 * under a ContentSelectionProvider.
 */
export const useContentSelectionDispatch = () => {
  return useContext(ContentSelectionStateDispatch);
};

export interface ContentSelectionProviderProps {
  initialValue: ContentSelectionContextValue;
  children?: ReactNode;
}

/**
 * Provides a shared selection context for components that consume this provider. Components can
 * consume and dispatch changes to the current selection using the hooks.
 * @param initialValue
 * @param children
 * @constructor
 */
export const ContentSelectionProvider = ({
  initialValue,
  children,
}: ContentSelectionProviderProps) => {
  const [state, setState] = useState<ContentSelectionContextValue>(initialValue);
  return (
    <ContentSelectionStateContext.Provider value={state}>
      <ContentSelectionStateDispatch.Provider value={setState}>
        {children}
      </ContentSelectionStateDispatch.Provider>
    </ContentSelectionStateContext.Provider>
  );
};
